import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import "../../styles/filesComponent.pc.css";
import { Context } from "../../../index";
import "../../Project/Files/Files.css";
import "./TaskFiles.css";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { AppContext } from "../../../App";
import { FILE_ICON, FileIconType } from "../../../constants/FileConst";
import unknown_svg from "../../../assets/FileIcons/unknown.svg";
import { handleError } from "../../../helpers/ErrorLib/handleError";
import Button from "../../UiLib/Button/Button";
import { ReactComponent as Download} from "../../../assets/download.svg"
import { getFileUrlFromId } from "../../../helpers/getFileUrlFromId";
import { IFileDisplay, IFilesDisplay } from "../../../common/api_interfaces/files/IFilesDisplay";
import { IGetFilesFilter, IGetFilesTaskDisplayData } from "../../../common/api_interfaces/files/IGetFilesDisplayData";

import { ReactComponent as Pluss } from "../../../assets/images/addDefault.svg";
interface TaskFilesProps {
    userAccess: IFunctionPermissions[];
}

const TaskFiles: FC<TaskFilesProps> = ({ userAccess }) => {
    const { store } = useContext(Context);
    const { id } = useParams();
    const { showToast } = useContext(AppContext);

    const [files, setFiles] = useState<IFilesDisplay | undefined>(undefined);
    const [viewTaskFileInfo, setViewFileInfo] = useState(false);
    const [editTaskfile, setEditTaskfile] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const checkPermissions = useCallback(() => {
        try {
            const taskFileActionAccess = userAccess.find((access) => access.functionCode === "TaskFileAction");

            if (taskFileActionAccess) {
                const viewPermission = taskFileActionAccess.permissions.find(p =>
                    p.permissionCode === "view" && p.isGranted
                );
                const createPermission = taskFileActionAccess.permissions.find(p =>
                    p.permissionCode === "create" && p.isGranted
                );

                setViewFileInfo(!!viewPermission);
                setEditTaskfile(!!createPermission);
            }
        } catch (err) {
            console.error("Error processing userAccess:", err);
        }
    }, [userAccess]);

    const loadFiles = useCallback(async (filters: IGetFilesFilter) => {
        try {
            const req: IGetFilesTaskDisplayData = { filters };
            const response = await store.fileService.getFilesTaskDisplayData(req);
            setFiles(response.data);
        } catch (error) {
            handleError(error, {
                defaultMessage: "Непредвиденная ошибка при загрузке файлов",
                showToast: showToast
            });
        }
    }, [store.fileService, showToast]);

    const handleAddFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }

        if (!editTaskfile) {
            showToast("У вас нет прав на добавление файлов!");
            return;
        }

        try {
            await store.fileService.addFileTask(e.target.files?.[0], Number(id));
            await loadFiles({
                taskId: Number(id),
                name: "",
                fileType: [],
            });
        } catch (error: any) {
            const isFileTooLarge = error.response?.status === 413 || error.response?.data?.includes?.("413 Request Entity Too Large");
            if (isFileTooLarge) {
                showToast("Ошибка загрузки: Файл слишком большой");
            } else {
                handleError(error, {
                    defaultMessage: "Непредвиденная ошибка при добавлении файла",
                    showToast: showToast
                });
            }

        }
    }, [editTaskfile, store.fileService, id, loadFiles, showToast]);

    const handleButtonClick = () => {
        if (!editTaskfile) {
            showToast("У вас нет прав на добавление файлов!");
            return;
        }
        fileInputRef.current?.click();
    };

    function isFileIconType(type: string): type is FileIconType {
        return type in FILE_ICON;
    }

    const fileIcon = useCallback((fileType: string | undefined) => {
        if (!fileType) {
            return <img src={unknown_svg} alt="File icon" />;
        }

        const fileTypeClean = fileType.replace(/\./g, "").toLowerCase();

        if (isFileIconType(fileTypeClean)) {
            const pictureSrc = FILE_ICON[fileTypeClean];
            return <img src={pictureSrc} alt="File icon" />;
        }

        return <img src={unknown_svg} alt="File icon" />;
    }, []);

    const handleDownloadFile = useCallback((item: IFileDisplay) => {
        const link = document.createElement('a');
        link.href = getFileUrlFromId(item.id);
        link.download = item.name || 'file';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [getFileUrlFromId])

    useEffect(() => {
        checkPermissions();
    }, [checkPermissions]);

    useEffect(() => {
        const loadTaskFiles = async () => {
            await loadFiles({
                taskId: Number(id),
                name: "",
                fileType: [],
            });
        };

        loadTaskFiles();
    }, [id, store.updateFile, loadFiles]);

    const filesList = useMemo(() => {
        if (!viewTaskFileInfo || !files?.files?.length) return null;

        return files.files.map((item) => (
            <div key={item.id} className="files-widget-items-container__block">
                <div className="files-widget-items-container__name">
                    <div className="files-widget-items-container__img">
                        {fileIcon(item.type)}
                    </div>
                    <div className="files-widget-file_item_name--text">
                        <a
                            title={`${item.name}${item.type}`}
                            className="files-widget-file_item_name--text__curent"
                        >
                            {item.name}{item.type}
                        </a>
                    </div>
                </div>
                <div className="task__component--right__menu--files__size">
                    <Button
                        id={viewTaskFileInfo ? "button-download" : "button-download-gray"}
                        callBack={() => handleDownloadFile(item)}
                        image={<Download />}
                    />
                </div>
            </div>
        ));
    }, [files, viewTaskFileInfo, fileIcon]);

    return (
        <div className="task__component--right__menu">
            <div className="task__component--right__menu--files">
                <h2 className="task__component--right__menu--files__name">
                    Файлы - {files?.totalRecords || 0}
                </h2>
                <div className="task__component--right__menu--files__btn">
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleAddFile}
                    />
                    <Button
                        id={editTaskfile ? "button-circle-add" : "button-circle-add-gray"}
                        callBack={handleButtonClick}
                        image={<Pluss />}
                    />
                </div>
            </div>
            <div>
                <div id="files-widget-items-container" className="files-widget-items-container__scroll">
                    {filesList}
                </div>
            </div>
        </div>
    );
};

export default observer(TaskFiles);